import { Component, Input, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';

import { Provider } from '../../../models';
import { Professional } from '../../../models/professional';
import { selectProfessionalsSelectedBenefit } from '../../../state/professionals/professionals.selectors';
import { StringUtils } from '../../../shared/utils/utils';
import { ConfigService } from '../../../services/config.service';

@Component({
  selector: 'app-professional-details-view',
  templateUrl: './professional-details-view.component.html',
  styleUrl: './professional-details-view.component.scss',
})
export class ProfessionalDetailsViewComponent {
  @Input() professional!: Professional;
  @Input() province!: string;

  private subscription = new Subscription();
  resolvedSelectedBenefit!: string;
  dateFormat: string = 'MMMM d, y';

  constructor(
    private store: Store,
    private router: Router,
    private configService: ConfigService
  ) {
    this.dateFormat = this.configService.getDateFormat();
  }

  ngOnInit(): void {
    const benefitSub = this.store
      .select(selectProfessionalsSelectedBenefit)
      .subscribe((benefit) => {
        this.resolvedSelectedBenefit = benefit;
      });
    this.subscription.add(benefitSub);
  }

  generateLinkHtml(url: string, text: string): string {
    return `
      <a href="${url}" target="_blank" rel="noopener" class="document-link-container">
        <img src="assets/images/link-icon.svg" alt="Link Icon" class="document-link-icon" />
        <span>${text}</span>
      </a>
    `;
  }

  getDocumentHtml(document: any): string {
    let content = document?.content || document?.title || '';

    if (document?.content && document?.content.startsWith('http')) {
      return this.generateLinkHtml(document.content, content);
    }
    if (document?.url) {
      return this.generateLinkHtml(document.url, content);
    }
    return content;
  }

  hasValidWorkplaces: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['professional']) {
      this.hasValidWorkplaces =
        this.professional?.workplaces?.some((w) => !!w?.provider?.name) ||
        false;
    }
  }

  navigateToDisciplines(professional: Professional) {
    this.router.navigate(['/disciplines'], {
      state: {
        searchData: {
          isSearchByStateParams: true,
          professional,
        },
      },
      queryParams: {
        ben: this.resolvedSelectedBenefit,
        prov: this.province,
        n: professional.name,
      },
    });
  }

  navigateToProvider(provider: Provider) {
    if (!provider) return;
    this.router.navigate(['/providers'], {
      queryParams: {
        ben: this.resolvedSelectedBenefit,
        prov: this.province,
        n: provider.name,
        id: provider.id,
      },
    });
  }

  formatUrl(url: string): string {
    return StringUtils.formatUrl(url);
  }

  getAddress(provider: Provider) {
    if (!provider) return null;
    const { address, address1, address2, address3, address4 } = provider;

    if (address) return address;

    const combinedAddress = [address1, address2, address3, address4]
      .filter((part) => !!part)
      .join(' ');

    return combinedAddress.length > 0 ? combinedAddress : null;
  }

  formatAuthorizations(string: string): string[] {
    return StringUtils.parseSemiColonSegments(string);
  }

  get concernTypes(): string {
    const concernTypes = this.professional?.events
      ?.map((e) => e?.type)
      ?.filter((type) => !!type);

    return concernTypes?.length
      ? concernTypes
          .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
          .join(', ')
      : 'Concern';
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
